import React from 'react'
import { Modal } from 'components/ui/Modal'
import { Badge } from 'components/ui/Badge'
import { Button } from 'components/ui/Button'
import { useTranslation } from 'react-i18next'
import { Calendar, Clock, MapPin, User, Link as LinkIcon, FileText, Briefcase } from 'lucide-react'
import type { InterviewEvent } from './InterviewCalendar'

interface InterviewDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  interview: InterviewEvent | null
}

export function InterviewDetailsModal({ isOpen, onClose, interview }: InterviewDetailsModalProps) {
  const { t } = useTranslation()

  if (!interview) return null

  const formatDateTime = (date: Date | string) => {
    const d = new Date(date)
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  }

  const formatTime = (date: Date | string) => {
    const d = new Date(date)
    return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  }

  const startDate = interview.start ? new Date(interview.start) : null
  const endDate = interview.end ? new Date(interview.end) : null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('offerDetail.calendar.interviewDetails', 'Interview Details')}
      size="md"
    >
      <div className="space-y-6">
        {/* Title */}
        <div>
          <h3 className="text-xl font-semibold text-foreground mb-2">{interview.title}</h3>
          {(interview.stepName || interview.extendedProps?.stepName) && (
            <Badge variant="secondary" className="mt-2">
              {interview.stepName || interview.extendedProps?.stepName}
            </Badge>
          )}
        </div>

        {/* Offer */}
        {(interview.extendedProps?.offerTitle) && (
          <div className="flex items-start gap-3">
            <Briefcase className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground">
                {t('offerDetail.calendar.offer', 'Offer')}
              </p>
              <p className="text-base text-foreground">
                {interview.extendedProps.offerTitle}
              </p>
            </div>
          </div>
        )}

        {/* Date & Time */}
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <Calendar className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground">
                {t('offerDetail.calendar.date', 'Date')}
              </p>
              <p className="text-base text-foreground">
                {startDate ? formatDateTime(startDate) : t('common.notSpecified', 'Not specified')}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground">
                {t('offerDetail.calendar.time', 'Time')}
              </p>
              <p className="text-base text-foreground">
                {startDate && endDate
                  ? `${formatTime(startDate)} - ${formatTime(endDate)}`
                  : startDate
                  ? formatTime(startDate)
                  : t('common.notSpecified', 'Not specified')}
              </p>
            </div>
          </div>
        </div>

        {/* Candidate */}
        {(interview.candidateName || interview.extendedProps?.candidateName) && (
          <div className="flex items-start gap-3">
            <User className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground">
                {t('offerDetail.calendar.candidate', 'Candidate')}
              </p>
              <p className="text-base text-foreground">
                {interview.candidateName || interview.extendedProps?.candidateName}
              </p>
            </div>
          </div>
        )}

        {/* Interviewer */}
        {(interview.interviewerName || interview.extendedProps?.interviewerName) && (
          <div className="flex items-start gap-3">
            <User className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground">
                {t('offerDetail.calendar.interviewer', 'Interviewer')}
              </p>
              <p className="text-base text-foreground">
                {interview.interviewerName || interview.extendedProps?.interviewerName}
              </p>
            </div>
          </div>
        )}

        {/* Location */}
        {(interview.location || interview.extendedProps?.location) && (
          <div className="flex items-start gap-3">
            <MapPin className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground">
                {t('offerDetail.calendar.location', 'Location')}
              </p>
              <p className="text-base text-foreground">
                {interview.location || interview.extendedProps?.location}
              </p>
            </div>
          </div>
        )}

        {/* Meeting Link */}
        {(interview.meetingLink || interview.extendedProps?.meetingLink) && (
          <div className="flex items-start gap-3">
            <LinkIcon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground mb-1">
                {t('offerDetail.calendar.meetingLink', 'Meeting Link')}
              </p>
              <a
                href={interview.meetingLink || interview.extendedProps?.meetingLink || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline text-base break-all"
              >
                {interview.meetingLink || interview.extendedProps?.meetingLink}
              </a>
            </div>
          </div>
        )}

        {/* Notes */}
        {(interview.notes || interview.extendedProps?.notes) && (
          <div className="flex items-start gap-3">
            <FileText className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground mb-1">
                {t('offerDetail.calendar.notes', 'Notes')}
              </p>
              <p className="text-base text-foreground whitespace-pre-wrap">
                {interview.notes || interview.extendedProps?.notes}
              </p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end pt-4 border-t">
          <Button onClick={onClose} variant="outline">
            {t('common.close', 'Close')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

